// MCP servers — synthetic scope dim derived from PROMPT_TOOL_CALL_NAMES.
//
// Claude's MCP namespacing convention is `mcp__<server>__<tool>`, so
// the analytics-api's existing Array(String) tool-name projection
// already carries the server identifier — we just slice the names
// client-side. No backend addition required.
//
// Server "id" is the raw underscored segment (e.g. `claude_ai_Linear`)
// to keep it stable and indexable. Display labels strip the `mcp__`
// noise and apply a small lookup table for known servers, with a
// sensible fallback for unknown ones.
//
// Exports `mcpServerAdapter` (DimAdapter) for registration in
// dimAdapters.js; the dispatch sites in analyticsClient/apiClient
// don't need to know about this dim specifically.
import {
    bucketSecondsForRange,
    buildPromptRootAndFilter,
    densifyBuckets,
    rangeBoundsMs,
    rangeToTimeRange,
    rowsToObjects,
    runQuery as runAnalyticsQuery,
} from "./analyticsClient.js";
import { countRowLocalPromptGroups } from "./logicalPromptGroups.js";
import { promptCount } from "./promptMetrics.js";
import { shapeDimensionEntries } from "./dimAdapters.js";
import { compareDimEntries } from "./dimSort.js";
import { toApiRange } from "./shell.js";
// Pure name/label helpers live in their own dependency-free module so the
// on-the-fly extractors (mcpServerDefs.js) can use them without loading this
// analytics-heavy module. Re-exported here so existing importers are unchanged.
import {
    extractServerFromToolName,
    labelForMcpServer,
    shortToolName,
    mcpServerProvenance,
} from "./mcpServerNames.js";
export { extractServerFromToolName, labelForMcpServer, shortToolName, mcpServerProvenance };

// Server ids are literal text, including LIKE wildcards and its escape character.
function escapeLikeLiteral(s) {
    return s.replace(/[\\%_]/g, (character) => `\\${character}`);
}

// LIKE predicate matching tool calls whose name has the
// `mcp__<id>__` prefix. On Array(String) the planner emits
// `arrayExists`, so a single LIKE pred = "any tool call by this
// server" — exactly the scope semantics we want.
function mcpLikePredicate(id, stepAlias = "p") {
    const escaped = escapeLikeLiteral(String(id || ""));
    return {
        pred: {
            field: { stepAlias, field: "DIMENSION_FIELD_PROMPT_TOOL_CALL_NAMES" },
            op: "OPERATOR_LIKE",
            value: `mcp\\_\\_${escaped}\\_\\_%`,
        },
    };
}

// Combine N server predicates into one FilterExpr. Multi-select
// within the dim is OR semantics.
export function mcpCombinePredicates(ids = [], stepAlias = "p") {
    const preds = (ids || []).map((id) => mcpLikePredicate(id, stepAlias));
    if (preds.length === 0) return null;
    if (preds.length === 1) return preds[0];
    return { orExpr: { children: preds } };
}

// Broad "any MCP tool call" filter used by the entries discovery
// query. Same escaping rules as the per-server form.
export const MCP_ANY_LIKE_VALUE = "mcp\\_\\_%";

// ── Entries pipeline ────────────────────────────────────────────────
//
// Discover tool names, then count distinct prompts matching each server.
// A prompt using several tools from one server contributes once. Sparklines
// remain lazy so discovery does not multiply tool names by time buckets.
//
// Returns the discovery spec and the servers it found, or null when the scope
// can't be carried or the query failed.
async function discoverServers({ apiScope = [], range, signalFilter = null, steer = null } = {}) {
    const apiRange = toApiRange(range);
    const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, steer });
    // Fail closed on a scope this PROMPT root can't carry rather than silently
    // aggregating across ALL models while a model filter is active (forward
    // guard — see buildPromptRootAndFilter's `unsupported` contract).
    if (unsupported.length) return null;
    const mcpAnyPred = {
        pred: {
            field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_TOOL_CALL_NAMES" },
            op: "OPERATOR_LIKE",
            value: MCP_ANY_LIKE_VALUE,
        },
    };
    // `signalFilter` (event_id IN the signal's fired prompts) is appended when a
    // signal is in scope so MCP-server discovery counts only signal-firing prompts.
    const filterParts = [scopeFilter, mcpAnyPred, signalFilter].filter(Boolean);
    const spec = {
        root,
        timeRange: rangeToTimeRange(apiRange),
        filter: filterParts.length === 1 ? filterParts[0] : { andExpr: { children: filterParts } },
        output: {
            dimensions: [
                { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_TOOL_CALL_NAMES" }, outputAlias: "tool_name" },
            ],
            // Aggregate discovery by tool; raw prompt rows could fill the row
            // limit with repeated tool names before other servers are seen.
            metrics: [promptCount("tool_prompts")],
        },
    };

    let rows = [];
    try {
        rows = rowsToObjects(await runAnalyticsQuery(spec));
    } catch (err) {
        console.warn("[mcp_server] discovery query failed:", err);
        return null;
    }

    const discoveredServers = new Set();
    for (const r of rows) {
        const server = extractServerFromToolName(String(r.tool_name || ""));
        if (!server) continue;
        discoveredServers.add(server);
    }
    return { spec, serverIds: [...discoveredServers] };
}

async function listEntries({ apiScope = [], offset = 0, limit = 100, search = "", sort = "activity", range, signalFilter = null, steer = null } = {}) {
    const discovered = await discoverServers({ apiScope, range, signalFilter, steer });
    if (!discovered) return shapeDimensionEntries({ entries: [], total: 0 });
    const { spec } = discovered;
    let { serverIds } = discovered;
    const trimmed = String(search || "").trim().toLowerCase();
    if (trimmed) {
        serverIds = serverIds.filter((id) => {
            const label = labelForMcpServer(id);
            return id.toLowerCase().includes(trimmed) || label.toLowerCase().includes(trimmed);
        });
    }

    let totalsByServer;
    try {
        const totals = await countRowLocalPromptGroups({
            root: spec.root, filter: spec.filter, timeRange: spec.timeRange,
            groups: serverIds.map((id) => ({ id, predicate: mcpLikePredicate(id) })),
            runQuery: runAnalyticsQuery, rowsToObjects,
        });
        totalsByServer = new Map(totals.map((row) => [row.id, row.value]));
    } catch (err) {
        console.warn("[mcp_server] prompt count query failed:", err);
        return shapeDimensionEntries({ entries: [], total: 0 });
    }

    const results = serverIds.map((id) => ({
        id,
        label: labelForMcpServer(id),
        value: totalsByServer.get(id) || 0,
        sparkline: null,
    }));

    const sorted = results.sort(compareDimEntries(sort));
    const sliced = sorted.slice(offset, offset + limit);
    return shapeDimensionEntries({ entries: sliced, total: sorted.length });
}

async function count({ apiScope = [], range, signalFilter = null, steer = null } = {}) {
    // Distinct servers reachable under scope: the discovery set's size. Every
    // discovered server has at least one matching prompt, so the per-server
    // counts cannot change it.
    const discovered = await discoverServers({ apiScope, range, signalFilter, steer });
    return discovered ? discovered.serverIds.length : 0;
}

// Trace-detail chip extraction. Walks the loaded trace's tools list
// for `mcp__<server>__<tool>` names and emits one chip per distinct
// server. ContextPanel renders these under the adapter's
// traceFacetGroupLabel ("MCP servers").
function extractTraceFacets(trace) {
    const tools = Array.isArray(trace?.tools) ? trace.tools : [];
    const counts = new Map();
    for (const t of tools) {
        const server = extractServerFromToolName(t);
        if (!server) continue;
        counts.set(server, (counts.get(server) || 0) + 1);
    }
    return [...counts.entries()]
        .map(([server, count]) => ({
            type:  server,
            label: labelForMcpServer(server),
            count,
            tooltip: `${count} MCP tool${count === 1 ? "" : "s"} from ${labelForMcpServer(server)}`,
        }))
        .sort((a, b) => b.count - a.count);
}

// ── Adapter export ─────────────────────────────────────────────────

export const mcpServerAdapter = {
    id:     "mcp_server",
    plural: "mcp_servers",
    label:  "MCP servers",
    description: "MCP servers derived from prompts' tool_call_names by the `mcp__<server>__<tool>` convention",

    predicate: mcpCombinePredicates,
    listEntries,
    count,
    labelFor: labelForMcpServer,

    // Trace-detail chips
    extractTraceFacets,
    traceFacetGroupLabel: "MCP servers",

    // Capability flags
    composesWithPromptGraph: true,
    bodyFitsInListProjection: true,
    countBatchable: false,
    // Row-local LIKE predicates on `p`, so visible rows' sparklines can share
    // one read as per-entry filtered counts (see getDimEntrySparkline).
    batchesEntrySparklines: true,
    // Same row-local predicate lets flow links count every server in one read.
    predicateIsRowLocal: true,
};
